import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const clusteringCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'Clustering',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.clusterings.`,
});

export async function getClusterings(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = clusteringCache.getFromMemory('all');
		if (memCached) return memCached;

		try {
			const legacyData = await iframeDataBridge.getStorageAsync('CLUSTERINGS');
			if (legacyData) {
				log('info', 'Clustering', '📦 Seeding Vue cache from legacy storage (CLUSTERINGS)');
				await clusteringCache.put('all', legacyData);
				clusteringCache.setInMemory('all', legacyData);
				return legacyData;
			}
		} catch (legacyError) {
			log('warn', 'Clustering', `⚠️ Legacy storage check failed: ${legacyError.message}`);
		}
	}

	try {
		const cached = await clusteringCache.get('all');
		if (cached && !forceRefresh) {
			clusteringCache.setInMemory('all', cached);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'Clustering', '📴 Offline: Returning cached clusterings');
				return cached;
			}

			setTimeout(() => refreshClusteringsInBackground(), 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'Clustering', '📴 Offline: No cached clusterings available');
			return [];
		}

		return await fetchClusteringsFromAPI();
	} catch (error) {
		log('error', 'Clustering', `❌ Error getting clusterings: ${error.message}`);

		try {
			const fallback = await clusteringCache.get('all');
			if (fallback) {
				log('info', 'Clustering', '💾 Using fallback cached clusterings');
				return fallback;
			}
		} catch (cacheError) {
			log('error', 'Clustering', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return [];
	}
}

async function fetchClusteringsFromAPI() {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/Clusterings'), { method: 'GET' }, 'tokenSales');

		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

		const clusterings = await response.json();
		await clusteringCache.put('all', clusterings);
		clusteringCache.setInMemory('all', clusterings);

		log('success', 'Clustering', `✅ Fetched and cached ${clusterings.length} clusterings`);
		return clusterings;
	} catch (error) {
		log('error', 'Clustering', `❌ Failed to fetch clusterings from API: ${error.message}`);
		throw error;
	}
}

async function refreshClusteringsInBackground() {
	try {
		await fetchClusteringsFromAPI();
		log('success', 'Clustering', '🔄 Background refresh of clusterings completed');
	} catch (error) {
		log('warn', 'Clustering', `⚠️ Background refresh failed: ${error.message}`);
	}
}

export async function refreshClusterings() {
	clusteringCache.clearMemory();
	await clusteringCache.clear();
}
